import type { Address, Hex } from 'viem';
import { decodeAdminCall, type AdminCall, type ContractName, type RoleName } from '@syntra/abi';
import { DEPLOYMENT as D } from '../config/env.ts';
import type { MessageKey } from '../i18n/index.tsx';
import { fmtBps, fmtDuration, fmtPct, fmtSig, shortAddr } from './format.ts';

type T = (key: MessageKey, vars?: Record<string, string | number>) => string;

const CONTRACT_KEY: Record<ContractName, MessageKey> = {
  staking: 'gov.c.staking',
  bonds: 'gov.c.bonds',
  referral: 'gov.c.referral',
  buyback: 'gov.c.buyback',
  registry: 'gov.c.registry',
  timelock: 'gov.c.timelock',
  treasury: 'gov.c.treasury',
  oracle: 'gov.c.oracle',
  lpLock: 'gov.c.lpLock',
  unknown: 'gov.c.unknown',
};

const ROLE_KEY: Record<RoleName, MessageKey> = {
  proposer: 'gov.r.proposer',
  executor: 'gov.r.executor',
  canceller: 'gov.r.canceller',
  pauser: 'gov.r.pauser',
  admin: 'gov.r.admin',
  treasurer: 'gov.r.treasurer',
  keeper: 'gov.r.keeper',
  staking: 'gov.r.staking',
  stakeFor: 'gov.r.stakeFor',
  other: 'gov.r.other',
};

/** A timelocked call in the user's language. */
export function describeCall(target: Address, data: Hex, t: T): string {
  return sentence(decodeAdminCall(target, data, D), t);
}

export function sentence(c: AdminCall, t: T): string {
  const C = t(CONTRACT_KEY[c.contract]);
  const bps = (v: number) => fmtBps(v);
  switch (c.kind) {
    case 'rates':
      return t('gov.rates', { aprs: c.aprs.map((a) => `${Math.round(a * 100) / 100}%`).join(' · ') });
    case 'fees':
      return t('gov.fees', { ins: c.stakeBps.map(bps).join(' · '), outs: c.unstakeBps.map(bps).join(' · '), recipient: shortAddr(c.recipient) });
    case 'maxRate':
      return t('gov.maxRate', { pct: fmtPct(c.ratePerEpoch, 3) });
    case 'referralWiring':
      return t('gov.referralWiring', { address: shortAddr(c.referral) });
    case 'market':
      return t(c.create ? 'gov.marketNew' : 'gov.market', { id: c.marketId ?? '', state: t(c.enabled ? 'gov.open' : 'gov.closed'), min: bps(c.roiMinBps), max: bps(c.roiMaxBps), cap: fmtSig(c.capacity) });
    case 'buyback':
      return t('gov.buyback', { trigger: bps(c.triggerBps), perTrade: fmtSig(c.maxPerTrade), perDay: fmtSig(c.maxPerDay), slip: bps(c.slippageBps), cooldown: fmtDuration(c.cooldown), bought: t(c.burnBought ? 'gov.burned' : 'gov.kept') });
    case 'minter':
      return t('gov.minter', { contract: t(CONTRACT_KEY[c.minter]), cap: fmtSig(c.capPerEpoch), n: c.burstEpochs, state: t(c.enabled ? 'gov.enabled' : 'gov.disabled') });
    case 'globalCap':
      return t('gov.globalCap', { cap: fmtSig(c.capPerEpoch), n: c.burstEpochs });
    case 'referralRates':
      return t('gov.referralRates', { direct: bps(c.directBps), ranks: c.ranks.map((r) => `${bps(r.rateBps)} ≥ $${fmtSig(r.thresholdUsd)}`).join(', '), minSide: bps(c.minSideBps) });
    case 'maxDepth':
      return t('gov.maxDepth', { n: c.depth });
    case 'capMultiplier':
      return t('gov.capMultiplier', { pct: bps(c.bps) });
    case 'pause':
      return t('gov.pause', { contract: C });
    case 'unpause':
      return t('gov.unpause', { contract: C });
    case 'upgrade':
      return t('gov.upgrade', { contract: C, impl: shortAddr(c.implementation) });
    case 'role':
      return t(c.grant ? 'gov.roleGrant' : 'gov.roleRevoke', { account: shortAddr(c.account), role: t(ROLE_KEY[c.role]), contract: C });
    case 'delay':
      return t('gov.delay', { time: fmtDuration(c.seconds) });
    case 'treasury':
      return t('gov.treasury', { fn: c.fn, args: c.args });
    case 'unknown':
      return t('gov.unknown', { selector: c.fn ?? c.selector, contract: C });
  }
}
